/**
 * Food search → add to the daily nutrition log. Ported from the Next app's
 * nutrition/search/page.tsx. Searches OpenFoodFacts (client-side) and posts the
 * chosen food to POST /api/nutrition/entries. Luxury design system.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Search, Plus } from 'lucide-react';
import { api } from '@/lib/api';
import { MEAL_SLOT_KEYS, withDefaults } from '@/lib/meals';

interface FoodResult {
  code: string;
  product_name: string;
  brands: string;
  nutriments: {
    'energy-kcal_100g': number;
    proteins_100g: number;
    carbohydrates_100g: number;
    fat_100g: number;
  };
}

export default function NutritionSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<FoodResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [labels, setLabels] = useState<Record<string, string>>(withDefaults(undefined));
  const [slot, setSlot] = useState<string>('meal_1');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get<{ mealLabels: Record<string, string> }>('/api/nutrition/meal-labels')
      .then((d) => setLabels(withDefaults(d.mealLabels)))
      .catch(() => {});
  }, []);

  async function handleSearch() {
    if (!query.trim()) return;
    setSearching(true);
    try {
      // Proxied through our worker (GET /api/nutrition/search) to avoid CORS.
      const data = await api.get<{ products: FoodResult[] }>(
        `/api/nutrition/search?q=${encodeURIComponent(query)}`,
      );
      setResults(data.products ?? []);
    } catch {
      /* leave results as-is */
    } finally {
      setSearching(false);
    }
  }

  async function addFood(food: FoodResult) {
    setSaving(true);
    const n = food.nutriments;
    try {
      await api.post('/api/nutrition/entries', {
        meal_slot: slot,
        food_name: `${food.product_name}${food.brands ? ` (${food.brands})` : ''}`,
        calories: Math.round(n['energy-kcal_100g'] || 0),
        protein_g: Math.round(n.proteins_100g || 0),
        carbs_g: Math.round(n.carbohydrates_100g || 0),
        fat_g: Math.round(n.fat_100g || 0),
        serving_size: 100,
        serving_unit: 'g',
        barcode: food.code,
      });
      navigate('/nutrition');
    } catch {
      setSaving(false);
    }
  }

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4">
      <div className="flex items-center gap-3 mb-4">
        <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--champagne)' }} aria-label="Back">
          <ArrowLeft size={22} />
        </button>
        <h1 className="display" style={{ fontSize: '1.9rem' }}>Search Food</h1>
      </div>

      {/* Meal slot */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-3 no-scrollbar">
        {MEAL_SLOT_KEYS.map((key) => (
          <button
            key={key}
            onClick={() => setSlot(key)}
            className="whitespace-nowrap px-3 py-1.5 text-xs font-medium transition-all flex-shrink-0"
            style={{
              borderRadius: 'var(--radius-sm)',
              background: slot === key ? 'var(--champagne)' : 'var(--surface)',
              color: slot === key ? 'var(--ink)' : 'var(--cream-muted)',
            }}
          >
            {labels[key]}
          </button>
        ))}
      </div>

      {/* Search bar */}
      <form onSubmit={(e) => { e.preventDefault(); handleSearch(); }} className="flex gap-2 mb-4">
        <div className="flex-1 relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--cream-muted)' }} />
          <input
            type="text"
            className="input"
            style={{ paddingLeft: 36 }}
            placeholder="Search foods…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
        <button type="submit" className="btn-gold px-4" disabled={searching}>
          {searching ? '…' : 'Search'}
        </button>
      </form>

      {/* Results */}
      <div className="flex flex-col gap-2">
        {results.map((food) => (
          <div key={food.code} className="card flex items-center justify-between">
            <div className="flex-1 min-w-0 mr-3">
              <p className="text-sm font-medium truncate" style={{ color: 'var(--cream)' }}>{food.product_name}</p>
              {food.brands && <p className="text-xs truncate" style={{ color: 'var(--cream-muted)' }}>{food.brands}</p>}
              <div className="flex gap-3 mt-1 text-xs" style={{ color: 'var(--cream-muted)' }}>
                <span>{Math.round(food.nutriments['energy-kcal_100g'] || 0)} cal</span>
                <span>P: {Math.round(food.nutriments.proteins_100g || 0)}g</span>
                <span>C: {Math.round(food.nutriments.carbohydrates_100g || 0)}g</span>
                <span>F: {Math.round(food.nutriments.fat_100g || 0)}g</span>
              </div>
            </div>
            <button
              onClick={() => addFood(food)}
              disabled={saving}
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: 'rgba(201,168,106,0.15)', color: 'var(--champagne)' }}
              aria-label={`Add ${food.product_name}`}
            >
              <Plus size={16} />
            </button>
          </div>
        ))}
        {!searching && results.length === 0 && query && (
          <p className="text-center text-sm mt-6" style={{ color: 'var(--cream-muted)' }}>
            No results. Try a different search.
          </p>
        )}
      </div>
    </div>
  );
}
